import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSectionsStore } from "../../stores/sections.store";
import { useFiltersStore } from "../../stores/filters.store";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";
import type { Section } from "../../interfaces/apiInterfaces/sections";
import ContentHeader from "../contentHeader/ContentHeader";
import SearchInput from "../searchInput/SearchInput";
import FilterDropdowns from "../filterDropdowns/FilterDropdowns";
import AddSectionModal from "../modal/AddSectionModal";
import EditSectionModal from "../modal/EditSectionModal";
import DeleteModal from "../modal/DeleteModal";
import ScrollbarArrows from "../table/ScrollbarArrows";
import Pagination from "../pagination/Pagination";
import "../jobDetails/JobDetails.scss";
import "./Sections.scss";

const Sections = () => {
  const navigate = useNavigate();
  const { getResourceValue } = useResources();
  const { sections, pagination, isLoading, error, fetchSections, deleteSection } = useSectionsStore();
  const { selectedCountryIsoCode, selectedGradeId, selectedLanguageIsoCode, selectedSubjectId, resetFilters } = useFiltersStore();
  const { canManageSections } = usePermissions();
  const { showError, showSuccess } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [sectionToEdit, setSectionToEdit] = useState<Section | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset filters when component mounts and cleanup on unmount
  useEffect(() => {
    // Reset filters when entering the page
    resetFilters();
    
    // Reset filters when leaving the page
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  // Show toast when section ID is copied
  const handleSectionIdCopy = () => {
    showSuccess(
      getResourceValue("copied", "Copied"),
      getResourceValue("section_id_copied", "Section ID copied to clipboard")
    );
  };

  // Helper function to check if a string is exactly 12 digits
  const is12DigitNumber = (value: string): boolean => {
    return /^\d{12}$/.test(value);
  };

  useEffect(() => {
    const baseParams = {
      page_number: currentPage,
      page_size: pageSize,
      ...(selectedCountryIsoCode && { country_iso_code: selectedCountryIsoCode }),
      ...(selectedGradeId && { grade_id: selectedGradeId }),
      ...(selectedLanguageIsoCode && { language_iso_code: selectedLanguageIsoCode }),
      ...(selectedSubjectId && { subject_id: selectedSubjectId }),
    };

    if (searchQuery.trim()) {
      const trimmedQuery = searchQuery.trim();
      // If the search query is exactly 12 digits, send as section_id
      // Otherwise, send as section_title
      if (is12DigitNumber(trimmedQuery)) {
        fetchSections({
          ...baseParams,
          section_id: trimmedQuery,
        });
      } else {
        fetchSections({
          ...baseParams,
          section_title: trimmedQuery,
        });
      }
    } else {
      // When not searching, use normal pagination with filters
      fetchSections(baseParams);
    }
  }, [currentPage, pageSize, fetchSections, searchQuery, selectedCountryIsoCode, selectedGradeId, selectedLanguageIsoCode, selectedSubjectId]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchQuery(inputValue);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // const handlePreviousPage = () => {
  //   if (currentPage > 1) {
  //     handlePageChange(currentPage - 1);
  //   }
  // };

  // const handleNextPage = () => {
  //   // Use API pagination data for both search and normal pagination
  //   const maxPages = pagination?.total_pages || 1;
    
  //   if (currentPage < maxPages) {
  //     handlePageChange(currentPage + 1);
  //   }
  // };

  const handleEdit = (section: Section, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click navigation
    setSectionToEdit(section);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click navigation
    setSectionToDelete(sectionId);
    setIsDeleteModalOpen(true);
  };


  const handleDeleteConfirm = async () => {
    if (!sectionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteSection(sectionToDelete);
      setIsDeleteModalOpen(false);
      setSectionToDelete(null);
      
      // Refresh sections list
      fetchSections({
        page_number: currentPage,
        page_size: pageSize,
      });
    } catch (error: any) {
      console.error("Failed to delete section:", error);
      // Extract error message from API response
      const errorMessage = error?.response?.data?.message || 
        getResourceValue("delete_section_failed", "Failed to delete section. Please try again.");
      showError(
        getResourceValue("delete_section_failed_title", "Delete Failed"),
        errorMessage
      );
    } finally {
      setIsDeleting(false);
    }
  };
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handlePaginationPageChange = (page: number) => {
    handlePageChange(page);
  };

  // Use sections directly from API (API handles filtering when searching)
  const displayedSections = sections;
  //const totalRecords = pagination?.total_records || 0;
  // const startRow = totalRecords > 0 ? (((pagination?.page_number || 1) - 1) * (pagination?.page_size || pageSize)) + 1 : 0;
  // const endRow = Math.min((pagination?.page_number || 1) * (pagination?.page_size || pageSize), totalRecords);

  if (isLoading && sections.length === 0) {
    return (
      <div className="page">
        <div className="loading-table">
          {getResourceValue("loading_sections", "Loading sections...")}
        </div>
      </div>
    );
  }

  if (error && sections.length === 0) {
    return (
      <div className="page">
        <div className="no-data">
          {getResourceValue("error_prefix", "Error:")} {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page sections-page">
         <div className="sections-filters">
        <FilterDropdowns 
          className="sections-filter-dropdowns" 
          showCountry={true}
          showGrade={true}
          showSubject={true}
          showLanguage={true}
        />
      </div>
        <ContentHeader
          title={getResourceValue("sections", "Sections")}
          headingLevel={1}
          actions={
            canManageSections ? (
              <>
                <button className="btn btn-green" onClick={() => setIsAddModalOpen(true)}>
                  <i className="f-icon plus-icon"></i>
                  {getResourceValue("add_section", "Add Section")}
                </button>
              </>
            ) 
          : null}
          className="content-header--sections-header"
        />
      
   

      <section className="table-container">

        <div className="table-wrapper">
          <div className="table-filter-container">
            <div className="search-section">
              <SearchInput
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={getResourceValue("search", "Search")}
              />
            </div>

            {pagination && (
              <div className="sorting-section">
                <div className="sorting-controls">
                  <Pagination
                    currentPage={pagination.page_number}
                    totalPages={pagination.total_pages}
                    totalRecords={pagination.total_records}
                    pageSize={pageSize}
                    onPageChange={handlePaginationPageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="loading-table">
              <p>{getResourceValue("loading_sections", "Loading sections...")}</p>
            </div>
          ) : (
            <>
              <ScrollbarArrows
                targetId="sections-table-scroll"
                tablesScroll={8}
              />
              <div className="table-scroll-container" id="sections-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{getResourceValue("section_id", "Section ID")}</th>
                      <th>{getResourceValue("title", "Title")}</th>
                      <th>{getResourceValue("country", "Country")}</th>
                      <th>{getResourceValue("language", "Language")}</th>
                      <th>{getResourceValue("grade", "Grade")}</th>
                      <th>{getResourceValue("subject", "Subject")}</th>
                      <th>{getResourceValue("term", "Term")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedSections.length > 0 ? (
                      displayedSections.map((section) => (
                        <tr 
                          key={section.section_id}
                          onClick={() => navigate(`/sections/${section.section_id}`)}
                          className="sections__table-row"
                        >
                          <td>
                            <span 
                              className="text-strong"
                              onCopy={handleSectionIdCopy}
                              style={{ userSelect: 'text' }}
                            >
                              {section.section_id}
                            </span>
                          </td>
                          <td>
                            <span className="text-strong">{section.section_title}</span>
                          </td>
                          <td>
                            <span className="text-strong">{section.country_localization_key}</span>
                          </td>
                          <td>
                            <span className="text-strong">{section.language_name.toUpperCase()}</span>
                          </td>
                          <td>
                            <span className="text-strong">{getResourceValue(section.grade_localization_key, section.grade_localization_key)}</span>
                          </td>
                          <td>
                            <span className="text-strong">{section.subject_name}</span>
                          </td>
                          <td>
                            <span className="text-strong">{section.term_name}</span>
                          </td>
                          <td>
                            {<div className="table-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="btn btn-white"
                                onClick={(e) => handleEdit(section, e)}
                              >
                                <i className="f-icon edit-icon"></i>
                                {getResourceValue("edit", "Edit")}
                              </button>
                              <button
                                className="btn btn-red"
                                onClick={(e) => handleDeleteClick(section.section_id, e)}
                              >
                                <i className="f-icon trash-icon"></i>
                                {getResourceValue("delete", "Delete")}
                              </button>
                            </div>}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div className="no-data">
                            {searchQuery 
                              ? getResourceValue("no_sections_found", "No sections found matching your criteria.")
                              : getResourceValue("no_sections_available", "No sections available.")
                            }
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      <AddSectionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          // Refresh sections list after successful addition
          fetchSections({
            page_number: currentPage,
            page_size: pageSize,
          });
        }}
      />

      {sectionToEdit && (
        <EditSectionModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSectionToEdit(null);
          }}
          onSuccess={() => {
            // Refresh sections list after successful edit
            fetchSections({
              page_number: currentPage,
              page_size: pageSize,
            });
            setSectionToEdit(null);
          }}
          section={sectionToEdit}
        />
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSectionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={getResourceValue("delete_section", "Delete Section")}
        message={
          sectionToDelete
            ? getResourceValue(
                "confirm_delete_section",
                `Are you sure you want to delete this section?`
              )
            : getResourceValue(
                "confirm_delete_section",
                "Are you sure you want to delete this section?"
              )
        }
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Sections;
